/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetSpendRulesOverviewRequest,
  GetSpendRulesOverviewSecurity,
} from "../models/operations/getspendrulesoverview.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSpendRulesOverviewQuery,
  prefetchSpendRulesOverview,
  queryKeySpendRulesOverview,
  SpendRulesOverviewQueryData,
} from "./spendRulesOverview.core.js";
export {
  buildSpendRulesOverviewQuery,
  prefetchSpendRulesOverview,
  queryKeySpendRulesOverview,
  type SpendRulesOverviewQueryData,
};

export type SpendRulesOverviewQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getSpendRulesOverview spendRules
 *
 * @remarks
 * Get budgets overview metrics: aggregate card numbers plus current-window usage per rule.
 */
export function useSpendRulesOverview(
  request?: GetSpendRulesOverviewRequest | undefined,
  security?: GetSpendRulesOverviewSecurity | undefined,
  options?: QueryHookOptions<
    SpendRulesOverviewQueryData,
    SpendRulesOverviewQueryError
  >,
): UseQueryResult<SpendRulesOverviewQueryData, SpendRulesOverviewQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildSpendRulesOverviewQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getSpendRulesOverview spendRules
 *
 * @remarks
 * Get budgets overview metrics: aggregate card numbers plus current-window usage per rule.
 */
export function useSpendRulesOverviewSuspense(
  request?: GetSpendRulesOverviewRequest | undefined,
  security?: GetSpendRulesOverviewSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SpendRulesOverviewQueryData,
    SpendRulesOverviewQueryError
  >,
): UseSuspenseQueryResult<
  SpendRulesOverviewQueryData,
  SpendRulesOverviewQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSpendRulesOverviewQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setSpendRulesOverviewData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SpendRulesOverviewQueryData,
): SpendRulesOverviewQueryData | undefined {
  const key = queryKeySpendRulesOverview(...queryKeyBase);

  return client.setQueryData<SpendRulesOverviewQueryData>(key, data);
}

export function invalidateSpendRulesOverview(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "overview", "get", ...queryKeyBase],
  });
}

export function invalidateAllSpendRulesOverview(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "overview", "get"],
  });
}
